import { computed, ref } from 'vue';
import DebtConversationApi from 'dashboard/api/debtConversation';

// Os seis status que o resolver emite (Debt::DebthubLinkResolver), mais o
// `{"enabled": false}` da conta que não é a do DEBTHUB. As frequências são
// MEDIDAS em produção (106.258 contatos da conta 1) e estão aqui porque elas
// decidem o desenho: `none` é o caso NORMAL, não um erro.
export const DEBTHUB_STATUS = {
  NONE: 'none', // 72,10%
  PROPOSED: 'proposed', // 25,89% com um candidato, 2,01% com vários
  CONFIRMED: 'confirmed',
  REJECTED: 'rejected',
  INSTITUTIONAL: 'institutional_number', // 0,24%
  UNAVAILABLE: 'unavailable', // degradação, chega como HTTP 200
};

/**
 * Correlação Chatwoot -> DEBTHUB de UMA conversa, com confirmação humana.
 *
 * DESEMPENHO: a leitura resolve sincronamente contra outro sistema. Este
 * composable só pode ser instanciado na ABERTURA de uma conversa — nunca no
 * caminho de render do board, onde os 42.594 cards da conta 1 virariam 42.594
 * chamadas HTTP externas (ver debtConversation.js).
 *
 * @param {Function} getConversationDisplayId devolve o display_id da conversa.
 * @returns {Object} estado e ações da correlação.
 */
export function useDebthubLink(getConversationDisplayId) {
  const payload = ref(null);
  const isLoading = ref(false);
  const hasError = ref(false);
  const isSubmitting = ref(false);
  const actionErrorKey = ref(null);

  // enabled=false é conta sem DEBTHUB: a tela não renderiza NADA (mostrar o
  // cadastro de outra empresa dentro da conversa seria vazamento entre
  // clientes, não detalhe cosmético). null enquanto carrega.
  const isEnabled = computed(() => payload.value?.enabled === true);
  const status = computed(() => payload.value?.status ?? null);
  const link = computed(() => payload.value?.link ?? null);

  // Ordenado por similaridade DESCENDENTE, nunca por nome: medido, o nome
  // aponta o vencedor em 3,00% dos casos ambíguos — ordenar por ele levaria o
  // humano a decidir errado. Cópia da lista, não sort no lugar: o payload é o
  // retrato da resposta e não pode ser reordenado por baixo.
  const candidates = computed(() =>
    [...(payload.value?.candidates ?? [])].sort(
      (a, b) => (b.similaridade ?? 0) - (a.similaridade ?? 0)
    )
  );

  const hasSingleCandidate = computed(
    () =>
      status.value === DEBTHUB_STATUS.PROPOSED && candidates.value.length === 1
  );

  const load = async () => {
    const displayId = getConversationDisplayId();
    if (!displayId) return;

    isLoading.value = true;
    hasError.value = false;
    actionErrorKey.value = null;
    try {
      const { data } = await DebtConversationApi.debthubLink(displayId);
      payload.value = data;
    } catch (error) {
      hasError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  // 409 = o estado do servidor mudou por baixo (já vinculado): recarregar é a
  // ação correta, e não repetir a escrita.
  const errorKeyForStatus = httpStatus => {
    if (httpStatus === 409) return 'ALREADY_LINKED';
    if (httpStatus === 503) return 'LOOKUP_UNAVAILABLE';
    if (httpStatus === 422) return 'INVALID';
    return 'GENERIC';
  };

  const write = async operation => {
    const displayId = getConversationDisplayId();
    if (!displayId || isSubmitting.value) return false;

    isSubmitting.value = true;
    actionErrorKey.value = null;
    try {
      const { data } = await operation(displayId);
      payload.value = data;
      return true;
    } catch (error) {
      const httpStatus = error?.response?.status;
      // A recarga vem ANTES de publicar a mensagem: `load` limpa o erro de
      // ação (é o que uma tentativa nova do usuário deve fazer), e publicar
      // antes dela apagaria justamente o aviso de que o estado mudou por
      // baixo. Medido: a mensagem de "já vinculado" nunca aparecia.
      if (httpStatus === 409) await load();
      actionErrorKey.value = errorKeyForStatus(httpStatus);
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  // NUNCA há vínculo automático: as três ações abaixo só existem atrás de um
  // clique. O sistema propõe; quem decide é quem está atendendo.
  const confirm = pessoaId =>
    write(displayId =>
      DebtConversationApi.confirmDebthubLink(displayId, pessoaId)
    );

  const reject = pessoaId =>
    write(displayId =>
      DebtConversationApi.rejectDebthubLink(displayId, pessoaId)
    );

  const undo = () =>
    write(displayId => DebtConversationApi.undoDebthubLink(displayId));

  return {
    payload,
    isLoading,
    hasError,
    isSubmitting,
    actionErrorKey,
    isEnabled,
    status,
    link,
    candidates,
    hasSingleCandidate,
    load,
    confirm,
    reject,
    undo,
  };
}
